import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { ConnectorConfiguration } from '@blaze-shared/connectors-domain';
import {
  CopyConnectorConfigEnvButtonComponent,
  DialogModule,
  EnvironmentIndicatorComponent,
} from '@blaze-shared/shared-feature';
import {
  ButtonBarComponent,
  ButtonComponent,
  PanelComponent,
  PanelRef,
} from '@blaze-shared/shared-ui';

import { BaseConnectorDialogComponent } from '../../__base-connector-dialog/base-connector-dialog.component';
import { prepareConnectorConfiguration } from '../../utils';

import { ConnectorForm, GenericOauthConfiguration } from './generic-oauth-connector-dialog.models';

@UntilDestroy()
@Component({
  selector: 'blaze-generic-oauth-connector-dialog',
  templateUrl: './generic-oauth-connector-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DialogModule,
    AsyncPipe,
    EnvironmentIndicatorComponent,
    CopyConnectorConfigEnvButtonComponent,
    ButtonBarComponent,
    NgTemplateOutlet,
    ButtonComponent,
    ReactiveFormsModule,
    PanelComponent,
  ],
  host: { class: 'data-connector-dialog' },
})
export class GenericOauthConnectorDialogComponent
  extends BaseConnectorDialogComponent<GenericOauthConfiguration>
  implements OnInit
{
  private panelRef = inject(PanelRef);

  serviceName = '';
  connectorForm: FormGroup<ConnectorForm>;

  get signInTitle(): string {
    return this.config?.serviceName ? `Login to ${this.config.serviceName}` : 'Login';
  }

  get signInButtonLabel(): string {
    return this.config?.serviceName ? `Sign in with ${this.config.serviceName}` : 'Sign in';
  }

  get proceedLabel(): string {
    return !this.config?.actionAfterDefinition || this.config.actionAfterDefinition === 'close'
      ? this.connectorId
        ? 'update connector'
        : 'create connector'
      : 'next';
  }

  constructor() {
    super();
    this.connectorForm = new FormGroup<ConnectorForm>({
      connectorName: new FormControl('', { nonNullable: true, validators: Validators.required }),
    });

    this.withOAuth();
  }

  ngOnInit(): void {
    if (!this.template?.target) {
      throw new Error('No template was provided');
    }
    if (!this.config) {
      throw new Error('No configuration was provided for oauth flow');
    }

    this.serviceName = this.config.serviceName;
    const configuration = this.getConfiguration(this.connector);
    this.fillForm(configuration);
    this.goToAuth({ reauthorize: true });
  }

  getConfiguration(connector?: ConnectorConfiguration | undefined): GenericOauthConfiguration {
    return {
      connectorId: connector?.id ?? '',
      connectorName: String(connector?.name ?? ''),
      authState: String(connector?.configuration?.authState ?? ''),
    };
  }

  fillForm(configuration: GenericOauthConfiguration): void {
    this.connectorId = configuration.connectorId;
    this.authState = configuration.authState;
    this.connectorForm.patchValue(
      {
        connectorName: configuration.connectorName,
      },
      { emitEvent: false },
    );
  }

  saveConnector(): void {
    if (this.connectorForm.invalid) {
      return;
    }

    const { connectorName } = this.connectorForm.value;

    if (!connectorName) {
      return;
    }

    const connectorConfig = prepareConnectorConfiguration(
      connectorName,
      this.template?.id,
      this.accountId,
      this.connector,
      this.authState ? { authState: this.authState } : {},
    );

    this.saveOrUpdateConnector(connectorConfig, this.connectorId)
      .pipe(untilDestroyed(this))
      .subscribe((connector) => {
        if (!connector) {
          return;
        }

        switch (this.config?.actionAfterDefinition) {
          case 'showDatasets':
            this.showDatasets.emit(connector);
            break;
          case 'close':
          default:
            this.panelRef.close();
            break;
        }
      });
  }
}
